import {
  useEventdetectorApi,
  useFetchAllEventdetectorDefinitions,
} from '@ais-ui/api';
import { useToast } from '@ais-ui/ui-core';
import { useEffect, useRef } from 'react';
import { BackendSeverity } from './NotificationContext';
import { Session } from '@ais-ui/auth';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';

interface EventDetail {
  event_ids: string[];
}

function severityToToastVariant(severity: string) {
  switch (severity) {
    case 'alert':
      return 'destructive';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
}

export const useEventNotification = (olSession: Session) => {
  const eventDetectorApi = useEventdetectorApi();
  const permissions = usePermissions();
  const { messages } = useLocalization();

  const { data: detectors } = useFetchAllEventdetectorDefinitions(
    permissions.events
  );
  const { toast } = useToast();

  // Use a ref to track failed event IDs across renders
  const failedEventIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleEventNotification = async (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;

      for (const eventId of customEvent.detail.event_ids) {
        if (!eventId) {
          console.error('Event ID of event notification is null or undefined');
          continue;
        }

        // Skip processing if we've already failed on this event ID
        if (failedEventIdsRef.current.has(eventId)) {
          console.log(`Skipping previously failed event ID: ${eventId}`);
          continue;
        }

        if (!detectors || !detectors.records) continue;

        try {
          // Fetch the event using the eventid from the eventNotification
          const response = await eventDetectorApi.getEventDetails({
            eventId: eventId,
          });

          const fetchedEvent = response.records?.[0];

          if (!fetchedEvent) {
            console.warn(`No event details found for event ID: ${eventId}`);
            continue;
          }

          // Get the id of the detector that triggered the event
          const detectorId = fetchedEvent.definition?.id;
          if (!detectorId) {
            console.warn(`No detector ID found for event ID: ${eventId}`);
            continue;
          }

          // Go through all detectors of the user to find the detector with the right id
          const detector = detectors.records.find(
            (det) => det.id === detectorId
          );

          if (detector) {
            // Get event details for notification
            const variant = severityToToastVariant(
              fetchedEvent.event?.severity as BackendSeverity
            );
            const shipName = fetchedEvent.event?.ship?.name || 'Unknown ship';

            const toastTitle = 'New event from ' + detector.name;
            const toastDescription = 'Triggered by ' + shipName;

            // Post notification
            toast({
              variant: variant,
              title: toastTitle,
              description: toastDescription,
            });
          }
        } catch (error) {
          // Handle API errors
          console.error(
            `Error fetching event details for event ID: ${eventId}`,
            error
          );

          // Add this event ID to our set of failed events
          failedEventIdsRef.current.add(eventId);

          // Show error toast to user - only once per event ID
          toast({
            variant: 'destructive',
            title: messages.genericToastTitleError,
            description: `Could not retrieve details for event with ID ${eventId}.`,
          });

          continue;
        }
      }
    };

    window.addEventListener('eventNotification', handleEventNotification);
    return () => {
      window.removeEventListener('eventNotification', handleEventNotification);
    };
  }, [detectors, eventDetectorApi, messages, olSession, toast]);
};
